import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import BookCall from "@/components/BookCall";
import WhatIs from "@/components/landing/WhatIs";
import HeroEmblem from "@/components/landing/HeroEmblem";
import MeetSoren from "@/components/landing/MeetSoren";
import Faq from "@/components/landing/Faq";
import { useAuth } from "@/contexts/AuthContext";
import { scrollToSection } from "@/lib/scrollToSection";
import { ArrowRight, ExternalLink, ShieldCheck, Cpu, Coins, Globe2, ShieldAlert, CheckCircle2, AlertTriangle, Users } from "lucide-react";

export default function Landing() {
  const { user } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const referralSlug = params.get("ref") || params.get("r");
  const [form, setForm] = useState({ name: "", email: "", phone: "", telegram: "", country: "", introduced_by: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [started, setStarted] = useState(null);
  const [error, setError] = useState("");
  const [refInfo, setRefInfo] = useState(null);

  useEffect(() => {
    const slug = referralSlug || "stu";
    api.get(`/public/referral/${slug}`).then(({ data }) => setRefInfo(data)).catch(() => {});
  }, [referralSlug]);

  // Arriving from a header link on another public page: land on the section.
  useEffect(() => {
    const id = location.state?.scrollTo;
    if (id) requestAnimationFrame(() => scrollToSection(id));
  }, [location.state]);

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/leads", { ...form, referral_slug: referralSlug || refInfo?.referral_slug, source: "landing" });
      setStarted(data?.onboarding || null);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not submit");
    } finally { setSubmitting(false); }
  };

  const carypactUrl = refInfo?.carypact_url || "https://app.carypact.com/?inviteCode=4zR0Wd&suffix=E6c327Stu";

  return (
    <div className="landing-violet min-h-screen pb-20">
      <Header variant="public" />

      {/* Affiliate disclosure strip */}
      <div className="px-6 md:px-10 mt-2">
        <div className="max-w-[1400px] mx-auto">
          <div className="glass-sm px-4 py-2 flex items-center justify-between text-[11px] font-mono text-ink-2" style={{ background: "rgba(255,255,255,0.45)" }} data-testid="affiliate-disclosure-strip">
            <span className="flex items-center gap-2"><ShieldAlert size={12} style={{ color: "#B45309" }} /> <span><b>Independent CaryPact affiliate.</b> Not affiliated with CaryPact, BOT Chain, or Coinstore.</span></span>
            <span className="hidden md:inline">Tracking · Education · Forecasts only · No custody · No financial advice</span>
          </div>
        </div>
      </div>

      {/* HERO — heading + CTAs, with the spinning brand emblem alongside */}
      <section className="px-6 md:px-10 pt-6 md:pt-8">
        <div className="max-w-[1400px] mx-auto fade-up grid lg:grid-cols-[1.15fr_0.85fr] gap-8 lg:gap-12 items-center">
          <div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight max-w-4xl" data-testid="hero-title">
            Welcome to <span className="gradient-text whitespace-nowrap">BotChain CP</span>
          </h1>
          <div className="mt-4 mb-8 flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-base md:text-lg font-semibold flex items-center gap-2" data-testid="hero-join">
              <Users size={17} style={{ color: "var(--primary)" }} /> Join our community.
            </p>
            <span className="hidden sm:inline text-ink-3">·</span>
            <p className="text-sm md:text-base text-ink-2" data-testid="hero-support">
              Track every dollar. Recover principal first.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {user && (
              <Link to="/dashboard" className="btn-primary" data-testid="hero-cta-dashboard">
                Go to my dashboard <ArrowRight size={16} />
              </Link>
            )}
            <Link to="/flow" className={user ? "btn-secondary" : "btn-primary"} data-testid="hero-cta-flow">
              See the whole flow <ArrowRight size={16} />
            </Link>
            <Link to="/calculator" className="btn-secondary" data-testid="hero-cta-calculator">
              Run the profit calculator
            </Link>
            <Link to="/setup" className="btn-ghost" data-testid="hero-cta-smart-setup">
              Smart Setup (3 clicks) <ExternalLink size={14} />
            </Link>
            <BookCall className="btn-secondary" label="Book a call" />
          </div>
          {refInfo && (
            <div className="mt-6 pill self-start" data-testid="referral-badge">
              <span className="dot" style={{ background: "var(--primary)", boxShadow: "0 0 0 4px rgba(124,58,237,0.18)" }} />
              Your invitation
              <span className="text-ink-3 font-mono">· CaryPact code {refInfo.cary_invite_code}</span>
            </div>
          )}
          </div>
          <HeroEmblem />
        </div>
      </section>

      {/* What is BotChainCP — single combined explanation + disclaimer */}
      <section className="px-6 md:px-10 mt-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="glass-strong p-7 md:p-9 fade-up" data-testid="what-is-botchaincp">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="chip chip-blue">What is BotChainCP</span>
              <span className="chip chip-amber flex items-center gap-1.5"><ShieldAlert size={12} /> Disclaimer</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">
              An independent tracking &amp; forecasting dashboard for CaryPact participants.
            </h2>
            <p className="text-ink-2 leading-relaxed max-w-3xl mb-6">
              BotChainCP is a dashboard we built to keep our own CaryPact participation honest — track every wallet, log every stake, watch every withdrawal, and forecast what the position looks like under real risk scenarios. It is independent of and not affiliated with CaryPact, BOT Chain, or Coinstore. We don&apos;t custody, pool, or invest your funds. We don&apos;t promise returns. We don&apos;t provide financial, legal, or tax advice.
            </p>
            <div className="grid md:grid-cols-2 gap-6 pt-5" style={{ borderTop: "1px solid rgba(15,18,38,0.08)" }}>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-ink-3 mb-2.5">What it does</div>
                <ul className="space-y-2 text-sm text-ink-2">
                  {[
                    "Personal CA wallet & mining tracker",
                    "Flexible + fixed-term staking ledger",
                    "Withdrawal & USDT recovery tracker",
                    "Forecast tool with downside scenarios",
                    "Risk alerts & affiliate activity",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-2"><CheckCircle2 size={16} style={{ color: "#00C853" }} className="flex-shrink-0" /> {t}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-ink-3 mb-2.5">What it does not do</div>
                <ul className="space-y-2 text-sm text-ink-2">
                  {[
                    "Does not custody, pool or invest funds",
                    "Does not promise returns or guarantees",
                    "Not a fixed-income or passive-income product",
                    "Does not sign or execute trades for you",
                    "Does not provide financial advice",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-2"><AlertTriangle size={16} style={{ color: "#B45309" }} className="flex-shrink-0" /> {t}</li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-[11px] text-ink-3 mt-6 leading-relaxed max-w-3xl">
              CaryPact participation involves risk including loss of principal. CA token price and liquidity can fluctuate. Withdrawals may fail or be delayed. Past performance does not guarantee future results. Tax obligations are yours.
            </p>
          </div>
        </div>
      </section>

      {/* What is BOT Chain / What is CaryPact */}
      <WhatIs />

      {/* 3 pillars */}
      <section className="px-6 md:px-10 mt-20">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-3 gap-5">
          {[
            { icon: Cpu, title: "Mining ledger", desc: "Track daily CA earned per wallet. Manual entry or CSV upload. USD-equivalent computed automatically.", chip: "Ledger", chipClass: "chip-blue" },
            { icon: Coins, title: "Staking & forecast", desc: "Log flexible and fixed-term positions. Visualize principal release, rewards earned, and break-even projections.", chip: "Forecast", chipClass: "chip-pink" },
            { icon: ShieldCheck, title: "Recovery & risk", desc: "Every withdrawal logged. Track slippage. See USDT recovered vs deployed — under multiple CA price scenarios.", chip: "Recovery", chipClass: "chip-green" },
          ].map((f, i) => (
            <div key={i} className="glass p-7 fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="flex items-center justify-between mb-5">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(64,96,255,0.14), rgba(107,138,255,0.08))" }}>
                  <f.icon size={20} style={{ color: "var(--primary)" }} />
                </div>
                <span className={`chip ${f.chipClass}`}>{f.chip}</span>
              </div>
              <h3 className="font-display text-2xl font-bold mb-2 tracking-tight">{f.title}</h3>
              <p className="text-sm text-ink-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The honest math */}
      <section className="px-6 md:px-10 mt-20">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="chip chip-amber mb-5">The honest math</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight mb-5 tracking-tight">
              Your real return is <span className="gradient-text">USDT in your bank</span>, not CA on a screen.
            </h2>
            <p className="text-ink-2 max-w-md mb-7">
              CA token price can fluctuate. Liquidity can change. Withdrawals can fail or be delayed. BotChainCP&apos;s job is to keep you anchored to one question: <em>how much real USDT have I recovered, compared to how much I deployed?</em>
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["USDT deployed", "Your input"],
                ["USDT recovered", "Tracked"],
                ["Recovery %", "Calculated"],
                ["Unrecovered", "Residual risk"],
              ].map(([k, v]) => (
                <div key={k} className="kpi-tile">
                  <div className="kpi-label">{k}</div>
                  <div className="kpi-num text-2xl">{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-strong p-7 fade-up">
            <div className="chip chip-amber mb-4">Default risk warning</div>
            <h3 className="font-display text-2xl font-bold mb-3 tracking-tight">Three things we never claim</h3>
            <ul className="space-y-2.5">
              {["Guaranteed returns", "Principal protection", "Passive risk-free income"].map((t, i) => (
                <li key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/50">
                  <AlertTriangle size={18} style={{ color: "#DC2626" }} />
                  <span className="text-sm font-medium">{t}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-ink-3 mt-4 leading-relaxed">CaryPact participation involves risk including loss of principal. Past performance does not guarantee future results. Tax obligations are yours. BotChainCP is an independent affiliate.</p>
          </div>
        </div>
      </section>

      {/* Meet Søren — the bot, plus a few live questions before signing up */}
      <MeetSoren />

      {/* FAQ — swaps to the member set for anyone signed in */}
      <Faq />

      {/* Lead capture */}
      <section className="px-6 md:px-10 mt-20" id="signup">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="chip chip-blue mb-5">Request access</div>
            <h2 className="font-display text-4xl font-bold leading-tight tracking-tight mb-4">Get a 1:1 setup session.</h2>
            <p className="text-ink-2 max-w-md mb-6">
              Tell us about your situation. We&apos;ll walk you through wallet setup, the risk profile, the math, and how to use the dashboard to track your position.
            </p>
            <div className="glass p-5 flex items-start gap-3" style={{ borderRadius: 16 }}>
              <ShieldCheck size={20} style={{ color: "#00C853" }} className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-sm mb-1">Safety first</div>
                <div className="text-xs text-ink-2">Never share your wallet seed phrase. We never ask for it. Neither does CaryPact.</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            {submitted ? (
              <div className="glass-strong p-8 fade-up" data-testid="lead-submitted">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(0,200,83,0.16)" }}>
                  <CheckCircle2 size={28} style={{ color: "#00863A" }} />
                </div>
                <h3 className="font-display text-3xl font-bold tracking-tight">Thanks, {form.name.split(" ")[0] || "friend"}.</h3>
                {started?.emailed ? (
                  <p className="text-ink-2 mt-2 mb-6" data-testid="lead-emailed">
                    Check your inbox — we just sent your start link to <b className="font-mono text-[13px]">{form.email}</b>.
                    One click and you&apos;re in the 10-minute walkthrough. Someone from the team is watching your progress if you get stuck.
                  </p>
                ) : (
                  <p className="text-ink-2 mt-2 mb-6">
                    Start the 10-minute walkthrough whenever you&apos;re ready — someone from the team is watching your progress if you get stuck.
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  <Link to={`/join${referralSlug || refInfo?.referral_slug ? `?ref=${referralSlug || refInfo.referral_slug}` : ""}`}
                    className="btn-primary" data-testid="lead-start-onboarding">
                    Start the walkthrough <ArrowRight size={16} />
                  </Link>
                  <a href={carypactUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="lead-go-carypact">Explore CaryPact</a>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="glass-strong p-7 md:p-8 space-y-5" data-testid="lead-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs text-ink-3 font-medium block mb-1.5">Full name</span>
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-glass" placeholder="Jane Doe" data-testid="lead-name" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-ink-3 font-medium block mb-1.5">Email</span>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-glass" placeholder="you@example.com" data-testid="lead-email" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-ink-3 font-medium block mb-1.5">Phone number</span>
                    <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-glass" placeholder="+1 555 123 4567" data-testid="lead-phone" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-ink-3 font-medium block mb-1.5">Telegram (optional)</span>
                    <input value={form.telegram} onChange={(e) => setForm({ ...form, telegram: e.target.value })} className="input-glass" placeholder="@yourhandle" data-testid="lead-telegram" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-ink-3 font-medium block mb-1.5">Country / State</span>
                    <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="input-glass" placeholder="e.g. United States · California" data-testid="lead-country" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-ink-3 font-medium block mb-1.5">Who introduced you? (optional)</span>
                    <input value={form.introduced_by} onChange={(e) => setForm({ ...form, introduced_by: e.target.value })} className="input-glass" placeholder="Name of the person who showed you" data-testid="lead-introduced-by" />
                  </label>
                </div>
                <div className="text-xs text-ink-3">
                  If someone walked you through this, add their name — we&apos;ll connect you with them so you
                  keep the same guide. Came across it on your own? Leave it blank and we&apos;ll take it from here.
                </div>
                {error && <div className="chip chip-amber" data-testid="lead-error">{error}</div>}
                <button type="submit" disabled={submitting} className="btn-primary" data-testid="lead-submit">
                  {submitting ? "Submitting…" : "Request my walkthrough"} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer with full disclosure */}
      <footer className="px-6 md:px-10 mt-24">
        <div className="max-w-[1400px] mx-auto glass p-7">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="font-display text-xl font-bold tracking-tight">BotChainCP</div>
              <div className="text-xs text-ink-3 mt-1">Independent CaryPact affiliate · Tracking &amp; forecasting.</div>
            </div>
            <div className="flex gap-5 md:justify-end text-xs font-medium">
              <a href={carypactUrl} target="_blank" rel="noopener noreferrer">app.carypact.com</a>
            </div>
          </div>
          <hr className="divider my-5" />
          <div className="text-[11px] text-ink-3 font-mono leading-relaxed">
            BotChainCP is an independent CaryPact affiliate platform. It is not affiliated with, endorsed by, or operated by CaryPact, BOT Chain, Coinstore Exchange, or any related entity. BotChainCP does not custody, pool, or invest user funds; it is a tracking, forecasting, education, and affiliate operations dashboard. Forecasts are estimates only and are not guaranteed. CaryPact participation involves risk including potential loss of principal. CA token price can fluctuate, liquidity can change, staking terms may change, withdrawals may fail or be delayed. Past performance does not guarantee future results. Tax obligations are the participant&apos;s responsibility. This platform does not provide financial, legal, or tax advice. © 2026.
          </div>
        </div>
      </footer>
    </div>
  );
}
